import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Truck,
  FileText,
  Package,
  Users as UsersIcon,
  Pencil,
  Settings2,
  KeyRound,
  UploadCloud,
  PauseCircle,
  Trash2
} from 'lucide-react';
import { deactivateEmitente, getEmitente } from '../api/emitente';
import { getCertificado } from '../api/certificado';
import LoadingSpinner from '../components/LoadingSpinner';
import EmpresaHeader from '../components/EmpresaHeader';
import CertificadoStatusCard from '../components/CertificadoStatusCard';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';

interface ModuloCard {
  to: string;
  label: string;
  Icon: typeof Truck;
  accent: string;
  iconColor: string;
  borderHover: string;
}

export default function EmpresaHubPage() {
  const { id } = useParams();
  const emitenteId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { setEmitenteId } = useEmitenteAtivo();

  const emitQ = useQuery({
    enabled: !isNaN(emitenteId),
    queryKey: ['emitente', emitenteId],
    queryFn: () => getEmitente(emitenteId)
  });

  const certQ = useQuery({
    enabled: !isNaN(emitenteId),
    queryKey: ['emitente-cert', emitenteId],
    queryFn: () => getCertificado(emitenteId)
  });

  const desativar = useMutation({
    mutationFn: () => deactivateEmitente(emitenteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['emitentes'] });
      qc.invalidateQueries({ queryKey: ['emitente', emitenteId] });
      navigate('/emitentes');
    }
  });

  useEffect(() => {
    if (!isNaN(emitenteId)) setEmitenteId(emitenteId);
  }, [emitenteId, setEmitenteId]);

  if (emitQ.isLoading) {
    return (
      <div className="py-10 grid place-items-center">
        <LoadingSpinner label="Carregando empresa..." />
      </div>
    );
  }
  if (!emitQ.data) {
    return (
      <div className="card-surface p-6 text-center">
        <p>Empresa não encontrada.</p>
        <Link to="/emitentes" className="btn-primary mt-4 inline-flex">Voltar</Link>
      </div>
    );
  }

  const emit = emitQ.data;
  const modulosDoc: ModuloCard[] = [
    emit.emite_cte !== false && {
      to: `/empresa/${emit.id}/painel/cte`,
      label: 'CT-e',
      Icon: Truck,
      accent: 'bg-emerald-500/10',
      iconColor: 'text-emerald-400',
      borderHover: 'hover:border-emerald-500/40'
    },
    emit.emite_nfe !== false && {
      to: `/empresa/${emit.id}/painel/nfe`,
      label: 'NF-e',
      Icon: FileText,
      accent: 'bg-indigo-500/10',
      iconColor: 'text-indigo-400',
      borderHover: 'hover:border-indigo-500/40'
    },
    emit.emite_ctos !== false && {
      to: `/empresa/${emit.id}/painel/cte-os`,
      label: 'CT-e OS',
      Icon: Truck,
      accent: 'bg-violet-500/10',
      iconColor: 'text-violet-400',
      borderHover: 'hover:border-violet-500/40'
    },
    emit.emite_mdfe !== false && {
      to: `/empresa/${emit.id}/painel/mdfe`,
      label: 'MDF-e',
      Icon: FileText,
      accent: 'bg-fuchsia-500/10',
      iconColor: 'text-fuchsia-400',
      borderHover: 'hover:border-fuchsia-500/40'
    }
  ].filter(Boolean) as ModuloCard[];
  const modulos: ModuloCard[] = [
    ...modulosDoc,
    {
      to: `/pessoas?emitente=${emit.id}`,
      label: 'Clientes',
      Icon: UsersIcon,
      accent: 'bg-sky-500/10',
      iconColor: 'text-sky-400',
      borderHover: 'hover:border-sky-500/40'
    },
    {
      to: `/produtos?emitente=${emit.id}`,
      label: 'Produtos',
      Icon: Package,
      accent: 'bg-amber-500/10',
      iconColor: 'text-amber-400',
      borderHover: 'hover:border-amber-500/40'
    },
    {
      to: `/empresa/${emit.id}/editar`,
      label: 'Cadastro',
      Icon: Pencil,
      accent: 'bg-rose-500/10',
      iconColor: 'text-rose-400',
      borderHover: 'hover:border-rose-500/40'
    }
  ];

  return (
    <div className="space-y-4 animate-fade-in">
      <EmpresaHeader
        emitente={emit}
        rightSlot={
          <Link
            to={`/empresa/${emit.id}/editar`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
          >
            <Pencil className="h-3.5 w-3.5" />
            Editar
          </Link>
        }
      />

      <CertificadoStatusCard
        emitenteId={emit.id}
        cert={certQ.data}
        onUploaded={() => {
          qc.invalidateQueries({ queryKey: ['emitente-cert', emitenteId] });
          qc.invalidateQueries({ queryKey: ['certs-vencendo'] });
        }}
      />

      {/* MÓDULOS — grid denso, mobile-first */}
      <section>
        <div className="eyebrow mb-2">Módulos</div>
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2">
          {modulos.map((m) => (
            <Link
              key={m.to}
              to={m.to}
              className={`card-surface p-3 flex flex-col items-center justify-center gap-2 aspect-square md:aspect-auto md:h-24 ${m.borderHover} active:scale-[0.97] transition-all group text-center`}
            >
              <div className={`h-9 w-9 rounded-lg grid place-items-center ${m.accent} group-hover:scale-110 transition-transform`}>
                <m.Icon className={`h-4 w-4 ${m.iconColor}`} />
              </div>
              <span className="text-xs font-semibold text-slate-950 leading-tight dark:text-white">{m.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* AÇÕES SECUNDÁRIAS — collapsed no mobile via details */}
      <details className="card-surface group" open={false}>
        <summary className="list-none cursor-pointer p-3 md:p-4 flex items-center gap-2 select-none">
          <Settings2 className="h-4 w-4 text-neutral-400" />
          <span className="text-sm font-semibold text-slate-950 dark:text-white">Configurações e ações</span>
          <span className="ml-auto text-xs text-neutral-500 group-open:hidden">Abrir</span>
          <span className="ml-auto text-xs text-neutral-500 hidden group-open:inline">Fechar</span>
        </summary>
        <div className="px-3 md:px-4 pb-3 md:pb-4 pt-1 border-t border-slate-200 flex flex-wrap gap-2 dark:border-white/5">
          <Link to={`/empresa/${emit.id}/editar#numeracao`} className="toolbar-btn">
            <Settings2 className="h-3.5 w-3.5" />
            Numeração
          </Link>
          <Link to={`/configuracoes`} className="toolbar-btn">
            <KeyRound className="h-3.5 w-3.5" />
            SMTP / Credenciais
          </Link>
          <button
            type="button"
            onClick={() => alert('Importação CSV estará disponível em breve.')}
            className="toolbar-btn"
          >
            <UploadCloud className="h-3.5 w-3.5" />
            Importar CSV
          </button>
          {emit.ativo !== false ? (
            <button
              type="button"
              onClick={() => {
                if (confirm(`Inativar "${emit.razao_social}"?\n\nEla sai da lista de ativas, mas o histórico permanece.`)) {
                  desativar.mutate();
                }
              }}
              className="btn-amber text-xs !py-1.5"
              disabled={desativar.isPending}
            >
              <PauseCircle className="h-3.5 w-3.5" />
              {desativar.isPending ? 'Inativando...' : 'Inativar'}
            </button>
          ) : (
            <span className="badge-neutral">Inativa</span>
          )}
          <button
            type="button"
            onClick={() => alert('Exclusão permanente só por suporte. Use "Inativar".')}
            className="btn-danger text-xs !py-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Excluir
          </button>
        </div>
      </details>
    </div>
  );
}
